import React, { useState } from "react";
import axios from "axios";

const emptyTheme3 = {
  defaultDepartmentImage: "",
  defaultTypeImage: "",
  defaultTypeHeroImage: "",
  defaultTypeHeroTitle: "",
  defaultTypeHeroSubtitle: "",
  departments: {},
  types: {},
};

const ModifyBrandData = () => {
  const [brandName, setBrandName] = useState("");
  const [originalBrandName, setOriginalBrandName] = useState("");

  const [formData, setFormData] = useState({
    newBrandName: "",
    headerImage: null,
    logo: null,
    theme: "",
  });

  const [theme3Config, setTheme3Config] = useState(emptyTheme3);
  const [departmentKey, setDepartmentKey] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const [status, setStatus] = useState("");

  const handleTextChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    }
  };

  const loadBrand = async () => {
    if (!brandName.trim()) {
      setStatus("⚠️ Enter brand name first.");
      return;
    }

    try {
      setStatus("Loading brand...");

      const response = await axios.get(
        "https://api.malidag.com/api/brands/themes"
      );

      const foundBrand = response.data.find(
        (brand) =>
          brand?.brandName?.trim()?.toLowerCase() ===
          brandName.trim().toLowerCase()
      );

      if (!foundBrand) {
        setStatus("❌ Brand not found.");
        return;
      }

      setOriginalBrandName(foundBrand.brandName);

      setFormData({
        newBrandName: foundBrand.brandName || "",
        headerImage: null,
        logo: null,
        theme: foundBrand.theme || "",
      });

      setTheme3Config({
        ...emptyTheme3,
        ...(foundBrand.themeConfig || {}),
        departments: foundBrand.themeConfig?.departments || {},
        types: foundBrand.themeConfig?.types || {},
      });

      setStatus("✅ Brand loaded. You can edit now.");
    } catch (error) {
      console.error(error);
      setStatus("❌ Failed to load brand.");
    }
  };

  const updateTheme3Root = (name, value) => {
    setTheme3Config((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addDepartment = () => {
    const key = departmentKey.trim();
    if (!key) return;

    setTheme3Config((prev) => ({
      ...prev,
      departments: {
        ...prev.departments,
       [key]: prev.departments[key] || {
        image: "",
        title: "",
        subtitle: "",

        heroImage: "",
        heroTitle: "",
        heroSubtitle: "",
      },
      },
    }));

    setDepartmentKey("");
  };

  const updateDepartment = (key, field, value) => {
    setTheme3Config((prev) => ({
      ...prev,
      departments: {
        ...prev.departments,
        [key]: {
          ...prev.departments[key],
          [field]: value,
        },
      },
    }));
  };

  const removeDepartment = (key) => {
    setTheme3Config((prev) => {
      const next = { ...prev.departments };
      delete next[key];

      return {
        ...prev,
        departments: next,
      };
    });
  };

  const addType = () => {
    const key = typeKey.trim();
    if (!key) return;

    setTheme3Config((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [key]: prev.types[key] || {
          image: "",
          title: "",
          subtitle: "",
          heroImage: "",
          heroTitle: "",
          heroSubtitle: "",
        },
      },
    }));

    setTypeKey("");
  };

  const updateType = (key, field, value) => {
    setTheme3Config((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [key]: {
          ...prev.types[key],
          [field]: value,
        },
      },
    }));
  };

  const removeType = (key) => {
    setTheme3Config((prev) => {
      const next = { ...prev.types };
      delete next[key];

      return {
        ...prev,
        types: next,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");

    const targetBrand = originalBrandName || brandName.trim();

    if (!targetBrand) {
      setStatus("⚠️ Please load or enter the current brand name.");
      return;
    }

    const payload = new FormData();

    if (formData.newBrandName.trim()) {
      payload.append("newBrandName", formData.newBrandName.trim());
    }

    if (formData.theme.trim()) {
      payload.append("theme", formData.theme.trim());
    }

    if (formData.theme.trim() === "theme3") {
      payload.append("themeConfig", JSON.stringify(theme3Config));
    }

    if (formData.headerImage) {
      payload.append("headerImage", formData.headerImage);
    }

    if (formData.logo) {
      payload.append("logo", formData.logo);
    }

    if ([...payload.keys()].length === 0) {
      setStatus("⚠️ Please provide at least one field to update.");
      return;
    }

    try {
      const response = await axios.patch(
        `https://api.malidag.com/api/brands/update/${encodeURIComponent(
          targetBrand
        )}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setStatus(`✅ Brand updated successfully.`);
      setOriginalBrandName(response.data?.brand?.brandName || targetBrand);
    } catch (error) {
      console.error("❌ Error updating brand:", error);
      setStatus(
        error.response?.data?.error ||
          "❌ Error updating brand. Check the brand name or try again."
      );
    }
  };

  return (
    <div style={{ padding: "1rem", maxWidth: "900px", margin: "0 auto" }}>
      <h2>🛠 Modify Brand Data</h2>

      <label>Current Brand Name:</label>
      <input
        type="text"
        value={brandName}
        onChange={(e) => setBrandName(e.target.value)}
        placeholder="e.g. kitchenaid"
      />

      <button type="button" onClick={loadBrand} style={{ marginTop: "10px" }}>
        Load Brand
      </button>

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <hr />

        <label>New Brand Name:</label>
        <input
          type="text"
          name="newBrandName"
          value={formData.newBrandName}
          onChange={handleTextChange}
          placeholder="optional"
        />

        <label>Header Image:</label>
        <input
          type="file"
          name="headerImage"
          accept="image/*"
          onChange={handleFileChange}
        />

        <label>Logo:</label>
        <input
          type="file"
          name="logo"
          accept="image/*"
          onChange={handleFileChange}
        />

        <label>Theme:</label>
        <select name="theme" value={formData.theme} onChange={handleTextChange}>
          <option value="">Select theme</option>
          <option value="theme1">theme1</option>
          <option value="theme2">theme2</option>
          <option value="theme3">theme3</option>
        </select>

        {formData.theme === "theme3" && (
          <div
            style={{
              marginTop: "2rem",
              borderTop: "1px solid #ddd",
              paddingTop: "1rem",
            }}
          >
            <h3>Theme3 Config</h3>

            <label>Default Department Image URL:</label>
            <input
              type="text"
              value={theme3Config.defaultDepartmentImage || ""}
              onChange={(e) =>
                updateTheme3Root("defaultDepartmentImage", e.target.value)
              }
            />

            <label>Default Type Image URL:</label>
            <input
              type="text"
              value={theme3Config.defaultTypeImage || ""}
              onChange={(e) =>
                updateTheme3Root("defaultTypeImage", e.target.value)
              }
            />

            <label>Default Type Hero Image URL:</label>
            <input
              type="text"
              value={theme3Config.defaultTypeHeroImage || ""}
              onChange={(e) =>
                updateTheme3Root("defaultTypeHeroImage", e.target.value)
              }
            />

            <label>Default Type Hero Title:</label>
            <input
              type="text"
              value={theme3Config.defaultTypeHeroTitle || ""}
              onChange={(e) =>
                updateTheme3Root("defaultTypeHeroTitle", e.target.value)
              }
            />

            <label>Default Type Hero Subtitle:</label>
            <input
              type="text"
              value={theme3Config.defaultTypeHeroSubtitle || ""}
              onChange={(e) =>
                updateTheme3Root("defaultTypeHeroSubtitle", e.target.value)
              }
            />

            <hr />

            <h3>Departments</h3>

            <input
              type="text"
              value={departmentKey}
              onChange={(e) => setDepartmentKey(e.target.value)}
              placeholder="department key, example: mixers"
            />

            <button type="button" onClick={addDepartment}>
              Add Department
            </button>

            {Object.entries(theme3Config.departments || {}).map(
              ([key, dept]) => (
                <div
                  key={key}
                  style={{
                    marginTop: "1rem",
                    padding: "1rem",
                    border: "1px solid #ddd",
                  }}
                >
                  <strong>{key}</strong>

                  <input
                    type="text"
                    value={dept.image || ""}
                    onChange={(e) =>
                      updateDepartment(key, "image", e.target.value)
                    }
                    placeholder="Department image URL"
                  />

                  <input
                    type="text"
                    value={dept.title || ""}
                    onChange={(e) =>
                      updateDepartment(key, "title", e.target.value)
                    }
                    placeholder="Department title"
                  />

                  <textarea
                    value={dept.subtitle || ""}
                    onChange={(e) =>
                      updateDepartment(key, "subtitle", e.target.value)
                    }
                    placeholder="Department subtitle"
                    rows={4}
                    style={{ width: "100%" }}
                  />

                  <input
                      type="text"
                      value={dept.heroImage || ""}
                      onChange={(e) =>
                        updateDepartment(key, "heroImage", e.target.value)
                      }
                      placeholder="Department hero image URL"
                    />

                    <input
                      type="text"
                      value={dept.heroTitle || ""}
                      onChange={(e) =>
                        updateDepartment(key, "heroTitle", e.target.value)
                      }
                      placeholder="Department hero title"
                    />

                    <textarea
                      value={dept.heroSubtitle || ""}
                      onChange={(e) =>
                        updateDepartment(key, "heroSubtitle", e.target.value)
                      }
                      placeholder="Department hero subtitle"
                      rows={4}
                      style={{ width: "100%" }}
                    />

                  <button type="button" onClick={() => removeDepartment(key)}>
                    Remove Department
                  </button>
                </div>
              )
            )}

            <hr />

            <h3>Types</h3>

            <input
              type="text"
              value={typeKey}
              onChange={(e) => setTypeKey(e.target.value)}
              placeholder="type key, example: hand_mixers"
            />

            <button type="button" onClick={addType}>
              Add Type
            </button>

            {Object.entries(theme3Config.types || {}).map(([key, type]) => (
              <div
                key={key}
                style={{
                  marginTop: "1rem",
                  padding: "1rem",
                  border: "1px solid #ddd",
                }}
              >
                <strong>{key}</strong>

                <input
                  type="text"
                  value={type.image || ""}
                  onChange={(e) => updateType(key, "image", e.target.value)}
                  placeholder="Type card image URL"
                />

                <input
                  type="text"
                  value={type.title || ""}
                  onChange={(e) => updateType(key, "title", e.target.value)}
                  placeholder="Type card title"
                />

                <textarea
                  value={type.subtitle || ""}
                  onChange={(e) => updateType(key, "subtitle", e.target.value)}
                  placeholder="Type card subtitle"
                  rows={4}
                  style={{ width: "100%" }}
                />

                <input
                  type="text"
                  value={type.heroImage || ""}
                  onChange={(e) => updateType(key, "heroImage", e.target.value)}
                  placeholder="Type hero image URL"
                />

                <input
                  type="text"
                  value={type.heroTitle || ""}
                  onChange={(e) => updateType(key, "heroTitle", e.target.value)}
                  placeholder="Type hero title"
                />

                <textarea
                  value={type.heroSubtitle || ""}
                  onChange={(e) =>
                    updateType(key, "heroSubtitle", e.target.value)
                  }
                  placeholder="Type hero subtitle"
                  rows={4}
                  style={{ width: "100%" }}
                />

                <button type="button" onClick={() => removeType(key)}>
                  Remove Type
                </button>
              </div>
            ))}
          </div>
        )}

        <button type="submit" style={{ marginTop: "1rem" }}>
          Update Brand
        </button>
      </form>

      {status && <p style={{ marginTop: "1rem", color: "blue" }}>{status}</p>}
    </div>
  );
};

export default ModifyBrandData;